import axios from 'axios';

import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
import { EzJson } from '/ezlibrary/helpers/EzJson.js';
import { EzEnvironment } from '/ezlibrary/enums/EzEnvironment.js';
import { EzRegEx } from '/ezlibrary/helpers/EzRegEx.js';

import { EzVersionChecker } from '/ezlibrary/EzVersionChecker.js';

/**
 * @public @class {EzString} extends {EzStaticClass}
 * @description
 * A class of static utility methods and/or properties for url strings.
 * ---------------------------------------------------------------------------
 * Import with:
 *     import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
 * ---------------------------------------------------------------------------
 */
export class EzUrl extends EzStaticClass {
    /**
     * @static
     * @public @setter @property
     * Sets the website version
     *
     * @param {string} environment
     * A valid enum property value from EzEnvironment
     * Default: EzEnvironment.PRD ('prd')
     *
     * @deprecated
     * Migrate to: EzVersionChecker.ezWebsiteVersion
     */
    static get ezWebsiteVersion() {
        return EzVersionChecker.ezWebsiteVersion;
    }

    /**
     * @static
     * @public @method
     * Gets the window.location.pathname value if available. Otherwise, returns empty string.
     *
     * @returns {string}
     */
    static get windowLocationPathName() {
        return EzObject.isValid(window) && EzObject.isValid(window.location) && EzObject.isValid(window.location.pathname)
            ? window.location.pathname
            : EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Gets the window.location.protocol value if available. Otherwise, an empty string.
     *
     * @returns {string}
     */
    static get windowLocationProtocol() {
        return EzObject.isValid(window) && EzObject.isValid(window.location) && EzObject.isValid(window.location.protocol)
            ? window.location.protocol
            : EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Gets the window.location.host value if available. Otherwise, an empty string.
     *
     * @returns {string}
     */
    static get windowLocationHost() {
        return EzObject.isValid(window) && EzObject.isValid(window.location) && EzObject.isValid(window.location.host)
            ? window.location.host
            : EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Gets the window.location.search value if available. Otherwise, an empty string.
     *
     * @returns {string}
     */
    static get windowLocationSearch() {
        return EzObject.isValid(window) && EzObject.isValid(window.location) && EzObject.isValid(window.location.search)
            ? window.location.search
            : EzString.EMPTY;
    }

    /**
     * @public @method
     * Builds and returns the full page url from window.location properties.
     * @returns {string}
     */
    static get currentWindowLocationUrl() {
        return EzUrl.build`
            ${EzUrl.windowLocationProtocol}/
            ${EzUrl.windowLocationHost}/
            ${EzUrl.windowLocationPathName}
            ${EzUrl.windowLocationSearch}`;
    }

    /**
     * @public @property @getter
     * Gets if debug mode is turned on (true) or off (false)
     *
     * Always enabled for localhost and dev.ezclocker.com
     * @returns {boolean}
     */
    static get ezDebugMode() {
        return 'true' === EzUrl.getUrlParam('debug') ||
            'localhost' === window.location.hostname ||
            '127.0.0.1' === window.location.hostname ||
            'dev.ezclocker.com' === window.location.hostname;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the value for the 'email' param
     *
     * @returns {string}
     */
    static get emailParam() {
        return EzUrl.readUrlParam('email');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the value for the 'v' param.
     *
     * @returns {string}
     */
    static get versionParam() {
        return EzUrl.readUrlParam('v');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the value for the 'un' param
     *
     * @returns {string}
     */
    static get usernameParam() {
        return EzUrl.readUrlParam('un');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the value for the 'name' param
     *
     * @returns {string}
     */
    static get nameParam() {
        return EzUrl.readUrlParam('name');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the value for the 'name' param
     *
     * @returns {string}
     */
    static get employerNameParam() {
        return EzUrl.readUrlParam('employer-name');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the value for the 'source' param
     *
     * @returns {string}
     */
    static get sourceParam() {
        return EzUrl.readUrlParam('source');
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the value for the 'inviteToken' param
     *
     * @returns {string}
     */
    static get inviteTokenParam() {
        return EzUrl.readUrlParam('inviteToken');
    }

    /**
     * @public @method
     * Appends the param anem and param value to the provided url
     *
     * @param {string} url
     * @param {string} paramName
     * @param {string} paramValue
     * Default: EzString.EMPTY
     *
     * @returns {string}
     */
    static appendParamToUrl(url, paramName, paramValue = EzString.EMPTY) {
        if (!EzString.hasLength(url) || !EzString.hasLength(paramName)) {
            return url;
        }

        return -1 !== url.indexOf('?')
            ? `${url}&${paramName}=${EzString.stringOrEmpty(paramValue)}`
            : `${url}?${paramName}=${EzString.stringOrEmpty(paramValue)}`;
    }

    /**
     * @static
     * @public @method
     * Adds a path variable to the provided url
     *
     * @param {string} url
     * @param {string} pathVariableValue
     */
    static addPathVariable(url, pathVariableValue = EzString.EMPTY) {
        if (!EzString.hasLength(url) || !EzString.hasLength(pathVariableValue)) {
            return url;
        }

        return '/' === url[url.length - 1]
            ? `${url}${pathVariableValue}`
            : `${url}/${pathVariableValue}`;
    }

    /**
     * @private @field
     * Stores the cache of url params from the current browser url
     *
     * @type {object}
     */
    static #urlParamCache = null;
    /**
     * @public @property @getter
     * Gets
     *
     * @returns {object}
     */
    static get urlParamCache() {
        if (null == this.#urlParamCache) {
            EzUrl.cacheUrlParams();
        }

        return this.#urlParamCache;
    }
    /**
     * @public @property @setter
     * Sets
     *
     * @returns {object}
     */
    static set urlParamCache(urlParamCache) {
        this.#urlParamCache = EzObject.assignOrNull(urlParamCache);
    }

    /**
     * @public @Method
     * Obtains and caches all the url params and values on the current browser url
     */
    static cacheUrlParams() {
        EzUrl.currentUrlParams = {};

        const parser = document.createElement('a');

        parser.href = window.location.href;

        const query = parser.search.substring(1);

        const vars = query.split('&');

        for (const param of vars) {
            const pair = param.split('=');

            EzUrl.currentUrlParams[pair[0]] = pair[1];
        }

        this.urlParamCache = EzUrl.currentUrlParams;
    }

    /**
     * @private @field
     * Stores if the url file revision is enabled
     * Default: true
     *
     * @type {boolean}
     */
    static #enableFileRevision = true;
    /**
     * @public @property @getter
     * Gets if the url file revision is enabled
     *
     * @returns {boolean}
     */
    static get enableFileRevision() {
        return EzUrl.#enableFileRevision;
    }
    /**
     * @public @property @setter
     * Sets if the url file revision is enabled
     *
     * @param {boolean} enableFileRevision
     * Default: true
     */
    static set enableFileRevision(enableFileRevision = true) {
        EzUrl.#enableFileRevision = EzBoolean.booleanOrTrue(enableFileRevision);
    }

    /**
     * @static
     * @private @field
     * Stores the current enviornment
     * Default: EzEnvironment.PRD ('prd')
     * Accepts a valid enum property value from EzEnvironment:
     *  EzEnvironment.UNKNOWN ('unknown')
     *  EzEnvironment.loc ('loc')
     *  EzEnvironment.loc ('dev')
     *  EzEnvironment.loc ('dev2')
     *  EzEnvironment.loc ('qal')
     *  EzEnvironment.loc ('e2e')
     *  EzEnvironment.loc ('prf')
     *  EzEnvironment.loc ('stg')
     *  EzEnvironment.loc ('org')
     *  EzEnvironment.loc ('grn')
     *  EzEnvironment.loc ('blu')
     *  EzEnvironment.loc ('old')
     *  EzEnvironment.loc ('prd')
     *
     * @type {string}
     */
    static #ezEnvironment = EzEnvironment.PRD;
    /**
     * @static
     * @public @getter @property
     * Gets the current enviornment
     *
     * @returns {string}
     * A valid enum property value from EzEnvironment
     */
    static get ezEnvironment() {
        return this.#ezEnvironment;
    }
    /**
     * @static
     * @public @setter @property
     * Sets the current enviornment
     *
     * @param {string} environment
     * A valid enum property value from EzEnvironment
     * Default: EzEnvironment.PRD ('prd')
     */
    static set ezEnvironment(environment = EzEnvironment.PRD) {
        try {
            this.#ezEnvironment = EzEnvironment.ezNameOf(
                EzString.stringWithLengthOrDefault(
                    environment,
                    EzEnvironment.PRD));
        } catch (err) {
            globalThis.console.error(
                EzString.em`
                    Error: ${err.message}
                    The provided environment of ${environment} is not a valid
                    EzEnvironment. Falling back to default environment: EzEnvironment.PRD.`);

            this.#ezEnvironment = EzEnvironment.PRD;
        }
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker website's Spring Security sign-in path
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get baseWebsiteSignInPath() {
        return '/resources/j_spring_security_check';
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker website's Spring Security sign-out path
     * Includes a leading '/'
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get baseWebsiteSignOutPath() {
        return '/resources/j_spring_security_logout';
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker mobile website's base public path
     * Includes a leading '/'
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get baseMobileWebsitePath() {
        return '/m';
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker mobile website's base public path
     * Includes a leading '/'
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get baseMobileWebsitePublicPath() {
        return `${EzUrl.baseMobileWebsitePath}/p`;
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker mobile website's base public path
     * Includes a leading '/'
     * Excludes an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get baseMobileWebsiteSecurePath() {
        return `${EzUrl.baseMobileWebsitePath}/s`;
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker mobile website's base public path
     * Includes a leading '/'
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get baseWebsitePublicPath() {
        return '/public';
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker mobile website's base public path
     * Includes a leading '/'
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get baseWebsiteSecurePath() {
        return '/secure';
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker internal API's base path
     * Includes a leading '/'
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get baseInternalApiPath() {
        return '/_api';
    }

    /**
     * @static
     * @public @readonly @property
     * EzClocker internal API's base path
     * Includes a leading '/'
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     */
    static get basePublicApiPath() {
        return '/api';
    }

    /**
     * @static
     * @public @method
     * EzClocker internal API's base path
     * Includes a leading '/'
     * Does not include an ending '/'
     * Excludes the protocol, domain, and port)
     * @param {string} apiVersion
     * Default: 'v1'
     */
    static basePublicApiVersionPath(apiVersion = 'v1') {
        return `${EzUrl.basePublicApiPath}/${EzString.stringOrDefault(apiVersion, 'v1')}`;
    }

    /**
     * @static
     * @public @method
     * Builds a url from the provided parts
     * @param {string} protocol
     * URL protocol (will append :// if not already provided)
     * Default: https://
     * @param {string} domain
     * URL domain - DO NOT include any paths as any '/' in the domain get removed
     * Default: ezclocker.com
     * @param {number|string} port
     * If port is a number or non-empty string the code will pre-pend ':' if missing
     * Default: blank
     * @param {string} pathAndParams
     * Will prepend the path's starting / if missing
     * Default: blank
     */
    static buildUrl(
        protocol = 'https://',
        domain = 'ezclocker.com',
        port = EzString.EMPTY,
        pathAndParams = EzString.EMPTY) {

        if (!EzString.hasLength(protocol)) {
            protocol = 'https://';
        }

        if (!EzString.hasLength(domain)) {
            domain = 'ezclocker.com';
        }

        if (!protocol.endsWith('://')) {
            protocol = `${protocol}://`;
        }

        if (0 <= domain.indexOf('/')) {
            domain = domain.replaceAll('/', EzString.EMPTY);
        }

        let portStr = EzString.EMPTY;

        if (EzNumber.isNumber(port)) {
            portStr = `:${port.toString()}`;
        } else if (EzString.hasLength(port)) {
            portStr = port.startsWith(':')
                ? port
                : `:${port}`;
        }

        if (EzString.hasLength(pathAndParams) && !pathAndParams.startsWith('/')) {
            pathAndParams = `/${pathAndParams}`;
        }

        return `${protocol}${domain}${portStr}${EzString.stringOrEmpty(pathAndParams)}`;
    }

    /**
     * @static
     * @public
     * Removes all spaces and line feeds from the provided aTemplateLitteralValue param. Then returns
     * the trimmed value.
     *
     * Example:
     *     EzUrl.url`
     *         https://ezclocker.com
     *         /employer/100
     *         ?target-time-zone-id=UTC`
     *     Result string: 'https://ezclocker.com/employer/100?target-time-zone-id=UTC';
     *
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    static build(aStrings, ...aKeys) {
        let url = EzString.EMPTY;

        if (!EzArray.hasLength(aStrings)) {
            for (const aKey of aKeys) {
                if (EzString.isString(aKey)) {
                    url = `${url}${aKey.trim()}`.trim();
                } else {
                    url = `${url}${EzObject.isValid(aKey) ? aKey.toString().trim() : EzString.EMPTY}`.trim();
                }
            }

            return url;
        }

        for (let index = 0; index < aStrings.length; index++) {
            let aString = aStrings[index];

            if (EzString.isString(aString)) {
                aString = EzString.dropSTNR(
                    aString.trim());
            } else {
                aString = EzObject.isValid(aString)
                    ? EzString.dropSTNR(
                        aString.toString().trim())
                    : EzString.EMPTY;
            }

            let aKey;

            if (index < aKeys.length) {
                // Append the key if provided
                aKey = aKeys[index];

                if (EzString.isString(aKey)) {
                    url = `${url}${aString}${EzString.dropSTNR(aKey)}`.trim();
                } else {
                    url = `${url}${aString}${EzString.dropSTNR(EzObject.isValid(aKey) ? aKey.toString().trim() : EzString.EMPTY)}`.trim();
                }
            } else {
                url = `${url}${aString}`.trim();
            }
        }

        return url;
    }

    /**
     * @static
     * @public @method
     * Applies URL decoding to the provided string
     * Convenience method that forwards to EzString.decodeUrl
     *
     * @param {string} value
     * @param {undefined|null|boolean} replacePlusWithSpace
     * If true, replaces any + symbol with a space
     *
     * @returns {string}
     */
    static decodeURL(value, replacePlusWithSpace) {
        if (!EzString.hasLength(value)) {
            return EzString.EMPTY;
        }

        return EzBoolean.isTrue(replacePlusWithSpace)
            ? decodeURIComponent(value)
                .replaceAll('+', ' ')
            : decodeURIComponent(value);
    }

    /**
     * @static
     * @public @method
     * Applies URL encoding to the provided string
     * Convenience method that forwards to EzString.encodeUrl
     *
     * @param {string} value
     *
     * @returns {string}
     */
    static encodeURL(value) {
        if (!EzString.hasLength(value)) {
            return EzString.EMPTY;
        }

        return encodeURIComponent(value).replace(
            EzRegEx.MATCH_ALL_BANG_TICK_PARENTHESIS_ASTRIX,
            (c) => '%' + c.charCodeAt(0).toString(16));
    }

    /**
     * @static
     * @public @method
     * Obtains the current environment from the public api
     * @returns {Promise.resolve}
     */
    static async ezLoadEnvironmentId() {
        return EzPromise.asyncAction(
            async (finished) => {
                await axios.get(
                    EzUrl.build`
                        ${EzUrl.basePublicApiVersionPath('v1')}
                        /version`)
                    .then(
                        (response) => {
                            if (EzString.hasLength(response.environment)) {
                                EzUrl.ezEnvironment = response.environment.toLowerCase();
                            }

                            if (!EzString.hasLength(EzUrl.ezEnvironment) && EzString.hasLength(response.targetEnvironment)) {
                                EzUrl.ezEnvironment = response.targetEnvironment.toLowerCase();
                            }

                            if (!EzString.hasLength(EzUrl.ezEnvironment)) {
                                EzUrl.ezEnvironment = ezApi.ezclocker.ezUrlHelper.ezGetEnvironmentFromUrl().toLowerCase();
                            }

                            if (!EzString.hasLength(globalThis?.ezApi?.ezclocker?.ezNavigation?.ezEnvironment)) {
                                EzUrl.ezEnvironment = EzEnvironment.PRD;
                            }

                            return finished(EzUrl.ezEnvironment);
                        })
                    .catch(
                        (eResponse) => {
                            if (EzObject.isValid(globalThis?.console)) {
                                globalThis?.console?.warn(
                                    EzString.em`
                                        Encountered an unexpected exception while loading the current enviornment id:
                                        ${eResponse.message}
                                        Defaulting to production (prd) environment.
                                        [Error response: ${EzJson.toJson(eResponse)}]`);
                            }

                            EzUrl.ezEnvironment = 'prd';

                            return finished(EzUrl.ezEnvironment);
                        });
            });
    }

    /**
     * @static
     * @public @method
     * Applies the the / character as the last charcter of the provided path.
     * If the path already has / as the last charcter, the path is returned as is.
     * If the path is empty or null, a single / character is returned
     * @param {string|null} path
     * @returns {string}
     * A string value of the path with a single applied / at the end if needed.
     */
    static postSlash(path) {
        if (!EzString.hasText(path)) {
            return '/';
        }

        return path.endsWith('/')
            ? path
            : `${path}/`;
    }

    /**
     * @static
     * @public @method
     * Applies the the / character as the first charcter of the provided path.
     * If the path already has / as the first charcter, the path is returned as is.
     * If the path is empty or null, a single / character is returned
     * @param {string|null} path
     * @returns {string}
     * A string value of the path with a single / at the beginning if needed.
     */
    static preSlash(path) {
        if (!EzString.stringHasLength(path)) {
            return '/';
        }

        return path.startsWith('/')
            ? path
            : `/${path}`;
    }

    /**
     * @static
     * @public @Method
     * Reads a param's value from the current browser url
     *
     * @param {string} paramName
     * @param {undefined|null|boolean} decode
     * Default: true}
     *
     * @returns {string}
     */
    static readUrlParam(paramName, decode = true) {
        if (!EzString.hasLength(paramName)) {
            return EzString.EMPTY;
        }

        if (EzObject.isValid(EzUrl.urlParamCache?.[paramName])) {
            return EzBoolean.booleanOrTrue(decode)
                ? EzString.stringOrEmpty(
                    EzUrl.decodeURL(EzUrl.urlParamCache?.[paramName]))
                : EzString.stringOrEmpty(EzUrl.urlParamCache?.[paramName]);
        }

        return EzString.EMPTY;
    }

    /**
     * @public @Method
     * Reads a param's value from the current browser url
     * If the param does not exist, then the defaultValue is returned.
     *
     * @param {string} paramName
     *
     * @returns {string}
     */
    static readUrlParamOrDefault(paramName, defaultValue, decode = true) {
        if (!EzString.hasLength(paramName)) {
            return defaultValue;
        }

        let paramValue;

        if (EzObject.isValid(EzUrl.urlParamCache?.[paramName])) {
            paramValue = decode
                ? EzUrl.decodeURL(EzUrl.urlParamCache?.[paramName])
                : EzUrl.urlParamCache?.[paramName];
        } else {
            paramValue = EzString.EMPTY;
        }

        return EzString.stringOrDefault(
            paramValue,
            defaultValue);
    }

    /**
     * @static
     * @public @Method
     * Attempts to detect the enviornment from the browser url. Returns PRD by default.
     *
     * @returns {string}
     */
    static determineEnvironmentFromUrl() {
        const url = window.location.href;

        if (-1 !== url.indexOf('localhost') || -1 !== url.indexOf('127.0.0.1') || -1 !== url.indexOf('loc.ezclocker.com')) {
            return 'loc';
        }

        if (-1 !== url.indexOf('dev.ezclocker.com') || -1 !== url.indexOf('dev1.ezclocker.com') || -1 !== url.indexOf('dev2.ezclocker.com')) {
            return 'dev';
        }

        if (-1 !== url.indexOf('qal.ezclocker.com')) {
            return 'qal';
        }

        if (-1 !== url.indexOf('e2e.ezclocker.com')) {
            return 'e2e';
        }

        if (-1 !== url.indexOf('prf.ezclocker.com')) {
            return 'prf';
        }

        if (-1 !== url.indexOf('stage.ezclocker.com') || -1 !== url.indexOf('stg.ezclocker.com')) {
            return 'stg';
        }

        return 'prd';
    }

    /**
     * @static
     * @public @method
     * Uses the new URL object to return parts.
     * If the URL object is not available, the urlString is returned.
     * If the urlString is null or empty, empty string is returned.
     *
     * @param {string} urlPartName     *
     * Must be one of: protocol, host, hostname, port, pathname, search, searchParams, or username. Any other value
     * will result in an undefined response.
     * @param {string} urlString
     *
     * @returns {string}
     */
    static urlPart(urlPartName, urlString) {
        if (!EzString.hasLength(urlPartName)) {
            throw new EzBadParamException(
                'urlPartName',
                EzUrl,
                EzUrl.urlPart);
        }

        if (!EzString.hasLength(urlString)) {
            throw new EzBadParamException(
                'urlString',
                EzUrl,
                EzUrl.urlPart);
        }

        urlPartName = urlPartName.toLowerCase();

        const result = new URL(urlString)[urlPartName];

        return EzString.hasLength(result)
            ? result
            : EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Determines if the current window.location url has the /secure path.
     * @returns {boolean}
     */
    static get isLocationUrlEzClockerSecure() {
        if (!EzString.hasLength(EzUrl.windowLocationPathName)) {
            return false;
        }

        const securePath = '/secure';

        return EzUrl.windowLocationPathName.length >= securePath.length && EzUrl.windowLocationPathName.startsWith(securePath);
    }

    /**
     * @static
     * @public @method
     * Determines if the current window.location url has the /public path.
     * @returns {boolean}
     */
    static get isLocationUrlEzClockerPublic() {
        if (!EzString.hasLength(EzUrl.windowLocationPathName)) {
            return false;
        }

        const publicPath = '/public';

        return EzUrl.windowLocationPathName.length >= publicPath.length && EzUrl.windowLocationPathName.startsWith(publicPath);
    }

    /**
     * @public @method
     * Replaces the url param (paramName) in the provided url with paramName=paramValue
     *
     * @param {string} url
     * @param {string} paramName
     * @param {string|number} paramValue
     *
     * @returns {string}
     */
    static replaceUrlParam(url, paramName, paramValue) {
        if (!EzString.hasLength(paramName)) {
            throw new EzBadParamException(
                'paramName',
                EzUrl,
                EzUrl.replaceUrlParam);
        }

        if (!EzObject.isValid(paramValue)) {
            paramValue = EzString.EMPTY;
        }

        const pattern = new RegExp(`\\b(${paramName}=).*?(&|#|$)`);

        if (0 <= url.search(pattern)) {
            return url.replace(pattern, `$1${paramValue}$2`);
        }

        url = url.replace(/[?#]$/, EzString.EMPTY);

        const paramDelimiter = -1 === url.indexOf('?')
            ? '?'
            : '&';

        return `${url}${paramDelimiter}${paramName}=${paramValue}`;
    }

    /**
     * @static
     * @public @method
     * Removes the provided parameter from the provided url.
     *
     * @param {string} url
     * @param {string} parameter
     *
     * @returns {string}
     */
    static removeUrlParam(url, parameter) {
        if (!EzString.hasLength(url) || !EzString.hasLength(parameter)) {
            return url;
        }

        const urlParts = url.split('?');

        if (2 <= urlParts.length) {
            // Get first part, and remove from array
            const urlBase = urlParts.shift();

            // Join it back up
            const queryString = urlParts.join('?');

            const prefix = `${ezApi.ezEncode(parameter)}=`;

            const parts = queryString.split(/[&;]/g);

            // Reverse iteration as may be destructive
            for (let i = parts.length; i > 0; i--) {
                // Idiom for string.startsWith
                if (parts[i].lastIndexOf(prefix, 0) !== -1) {
                    parts.splice(i, 1);
                }
            }

            url = `${urlBase}?${parts.join('&')}`;
        }

        return url.endsWith('?')
            ? url.substring(0, url.length - 1)
            : url;
    }
}

globalThis.EzUrl = EzUrl;
